import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { captureSessionLog, detectDeviceType, readSessionLog, updateSessionLog, consumeSessionCodes } from "@/lib/sessionLog";
import { LANGUAGE_IDS, loadTranslations } from "@/i18n/translations";
import { marketFromPath } from "@/lib/marketRoutes";
import { evaluateQrValue } from "@/lib/qrRouting";

// Session-context: Bootstrap afgør Step 01 før den indlæser landespecifikke data.
// Cookie-attribution læses ind her, mens aktuelle Step 03–04-værdier ejes direkte
// af SessionContext.
const SessionContext = createContext(null);
const bootstrapRequests = new Map();
const loadBootstrap = (params) => {
  const key = new URLSearchParams(params).toString();
  const existing = bootstrapRequests.get(key);
  if (existing) return existing;
  const request = api.get("/public/bootstrap", { params });
  bootstrapRequests.set(key, request);
  request.finally(() => {
    if (bootstrapRequests.get(key) === request) bootstrapRequests.delete(key);
  }).catch(() => {});
  return request;
};

const languageFromValue = (value) => String(value || "").toLowerCase().split("-", 1)[0];

export function SessionProvider({ children }) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [languagePreference, setLanguagePreference] = useState("");
  const [geoRetry, setGeoRetry] = useState(0);
  const [translationPack, setTranslationPack] = useState(null);
  const languageCache = useRef(new Map());
  const visitTimestampRef = useRef("");
  const deviceTypeRef = useRef("");
  const referralCodeRef = useRef("");
  const bootstrapOriginRef = useRef({ pathname: "", countryId: "" });
  const [session, setSession] = useState({
    countryId: "DK",
    countryLabel: "Danmark",
    currency: "",
    languageId: "",
    vatPercentage: 25,
    b2bDiscountCard: 10,
    b2bDiscountInvoice: 20,
    invoiceTermsDays: 30,
    freightProductId: "",
    campaignActive: false,
    agentMode: false, // land uden PARAMETERS-række → agent-siden (ingen bestilling)
    fallbackMarket: "",
    fallbackPath: "",
    geoFailed: false, // IP-opslag fejlede → venteskærm (aldrig fallback til Danmark)
    productTexts: {}, // PRODUKTEKSTER for valgt sprog (map pr. ProductId) — dansk = basefelter
    products: [],
    content: null,
    seo: null,
    activeMarkets: [],
    adsConfig: {},
    sessionLog: null, // indholdet af cookien "invictus_session_log"
    utmCampaignCode: "NONE", // Step 07: konkret whitelisted kode eller NONE
    visitTimestamp: "", // Step 3: kun den aktive SessionContext
    deviceType: "", // Step 4: kun den aktive SessionContext
    referralCode: "None", // Step 5: kun den aktive SessionContext
    qrCode: "None", // Step 8: medbragt QR-sti (ellers "None")
    campaignBadgeVisible: false, // Step 07: gyldig kampagnekode + aktiv kampagne
    ready: false,
  });

  const loadLanguage = useCallback((languageId) => {
    const language = LANGUAGE_IDS.includes(languageId) ? languageId : "da";
    if (!languageCache.current.has(language)) {
      languageCache.current.set(language, loadTranslations(language));
    }
    return languageCache.current.get(language);
  }, []);

  const preloadOtherLanguages = useCallback((activeLanguage) => {
    const preload = () => LANGUAGE_IDS.filter((language) => language !== activeLanguage).forEach((language) => { void loadLanguage(language); });
    const scheduleIdle = () => {
      if (window.requestIdleCallback) window.requestIdleCallback(preload, { timeout: 4500 });
      else window.setTimeout(preload, 2500);
    };
    if (document.readyState === "complete") window.setTimeout(scheduleIdle, 1500);
    else window.addEventListener("load", scheduleIdle, { once: true });
  }, [loadLanguage]);

  useEffect(() => {
    // Admin kører uden landing-evaluering.
    if (pathname.startsWith("/admin")) {
      setSession((s) => ({ ...s, ready: true }));
      return;
    }

    const urlParams = new URLSearchParams(search);
    const marketRoute = marketFromPath(pathname);
    if (
      bootstrapOriginRef.current.pathname === "/"
      && marketRoute?.countryId === bootstrapOriginRef.current.countryId
    ) {
      bootstrapOriginRef.current = { pathname: "", countryId: "" };
      return undefined;
    }
    const readUrlParam = (name) => {
      for (const [key, value] of urlParams.entries()) {
        if (key.toLowerCase() === name.toLowerCase()) return value;
      }
      return "";
    };
    // Kun aftalte URL-værdier behandles: countryId i Bootstrap, UTM i session-log
    // og QR_Value i Step 08. Alle navne læses case-insensitivt.
    const countryOverride = readUrlParam("countryId");
    const qrValue = evaluateQrValue(readUrlParam("QR_Value"));
    localStorage.removeItem("invictus_country_override");
    // Step 5: ryd kun en gammel nøgle fra tidligere versioner; nye referral-koder
    // gemmes aldrig i sessionStorage.
    sessionStorage.removeItem("invictus_referral_code");
    const referralPathMatch = pathname.match(/^\/r\/([^/]+)\/?$/i);
    const referralFromPath = referralPathMatch ? referralPathMatch[1] : "";
    const ref = referralFromPath || referralCodeRef.current;
    const cookieLanguage = languageFromValue(readSessionLog()?.Language);
    const savedCookieLanguage = LANGUAGE_IDS.includes(cookieLanguage) ? cookieLanguage : "";
    const deviceLanguage = (navigator.language || "").toLowerCase();
    // Step 2: Cookiens sprog er autoritativt. Findes det ikke, vurderer
    // backend enhedssproget mod SITEPARAMETRE og falder ellers tilbage til
    // det evaluerede lands standardsprog.
    const preferredLanguage = savedCookieLanguage || deviceLanguage;
    let active = true;
    setSession((current) => current.geoFailed ? { ...current, geoFailed: false, ready: false } : current);
    setSession((s) => ({ ...s, ready: s.ready, geoFailed: false, fallbackMarket: "", fallbackPath: "" }));

    loadBootstrap({
      ref,
      countryId: countryOverride,
      marketCountryId: marketRoute?.countryId || "",
      preferredLanguage,
    })
      .then(async (bootstrapResponse) => {
        if (!active) return;
        const bootstrap = bootstrapResponse.data || {};
        const evaluated = bootstrap.session || {};
        const countryId = evaluated.country_id || "";
        const languageId = bootstrap.language_id || evaluated.default_language || "da";
        if (!visitTimestampRef.current) visitTimestampRef.current = new Date().toISOString();
        if (!deviceTypeRef.current) deviceTypeRef.current = detectDeviceType();
        const visitTimestamp = visitTimestampRef.current;
        const deviceType = deviceTypeRef.current;
        const pack = await loadLanguage(languageId);
        if (!active) return;
        const campaignActive = Boolean(evaluated.campaign_active);
        const referralCode = evaluated.referral_valid ? ref : "None";
        referralCodeRef.current = referralCode === "None" ? "" : referralCode;
        bootstrapOriginRef.current = { pathname, countryId };
        const sessionLog = captureSessionLog(evaluated.session_log, { countryId, campaignActive, language: languageId });
        const utmCampaignCode = String(sessionLog?.UTM_Campaigncode || "NONE");
        const campaignBadgeVisible = campaignActive
          && utmCampaignCode.toUpperCase() !== "NONE"
          && referralCode === "None";
        setTranslationPack(pack);
        setSession({
          countryId,
          countryLabel: evaluated.country_label || countryId,
          currency: String(evaluated.currency || "").toUpperCase(),
          languageId,
          qrCode: qrValue.code,
          vatPercentage: evaluated.vat_percentage ?? 25,
          b2bDiscountCard: evaluated.b2b_discount_card ?? 10,
          b2bDiscountInvoice: evaluated.b2b_discount_invoice ?? 20,
          invoiceTermsDays: evaluated.invoice_payment_terms_days ?? 30,
          freightProductId: String(evaluated.freight_product_id || ""),
          campaignActive,
          visitTimestamp,
          deviceType,
          referralCode,
          agentMode: countryId === "GB",
          fallbackMarket: "",
          fallbackPath: "",
          geoFailed: false,
          sessionLog: sessionLog || readSessionLog(),
          utmCampaignCode,
          campaignBadgeVisible,
          productTexts: bootstrap.product_texts || {},
          products: Array.isArray(bootstrap.products) ? bootstrap.products : [],
          content: bootstrap.country_content || null,
          seo: bootstrap.seo?.profile || null,
          activeMarkets: bootstrap.seo?.active_markets || [],
          adsConfig: bootstrap.ads_config || {},
          ready: true,
        });
        preloadOtherLanguages(languageId);
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 503 && err?.response?.data?.detail === "geo_unavailable") {
          setSession((s) => ({ ...s, geoFailed: true, ready: false }));
          return;
        }
        setSession((s) => ({ ...s, sessionLog: readSessionLog(), ready: true }));
      });
    return () => { active = false; };
  }, [pathname, search, languagePreference, geoRetry, loadLanguage, preloadOtherLanguages]);

  const setLanguageId = (lang) => {
    const language = languageFromValue(lang);
    if (!LANGUAGE_IDS.includes(language)) return;
    const sessionLog = updateSessionLog({ Language: language });
    setSession((current) => ({ ...current, languageId: language, sessionLog: sessionLog || current.sessionLog }));
    setLanguagePreference(language);
  };

  const setReferralCode = useCallback((code) => {
    const referralCode = String(code || "").trim();
    referralCodeRef.current = referralCode;
    setSession((current) => ({ ...current, referralCode: referralCode || "None" }));
  }, []);

  // Ved bekræftet køb ryddes kampagnekode i cookie/live-session og ReferralCode live.
  const consumeCodes = useCallback(() => {
    const next = consumeSessionCodes();
    referralCodeRef.current = "";
    setSession((s) => ({ ...s, referralCode: "None", utmCampaignCode: "NONE", campaignBadgeVisible: false, sessionLog: next || s.sessionLog }));
  }, []);

  // Behold senest viste sprogpakke under skift; nye sprog indlæses først ved behov.
  const t = (key) => translationPack?.[key] ?? "";

  // Venteskærm ved IP-opslagsfejl — brugeren venter og prøver igen
  if (session.geoFailed) {
    return (
      <div data-testid="geo-retry-screen" className="min-h-screen bg-[#0B0E12] flex items-center justify-center px-6">
        <div className="max-w-md text-center border border-brand-border p-10">
          <p className="font-display text-2xl font-extrabold uppercase tracking-wide text-slate-100">
            Et øjeblik…
          </p>
          <p className="text-slate-400 text-sm mt-4 leading-relaxed">
            We could not determine which country you are visiting from. You can try again or continue in Sweden.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              data-testid="geo-retry-button"
              onClick={() => setGeoRetry((attempt) => attempt + 1)}
              className="bg-brand-blue text-[#0B0E12] font-display font-bold uppercase tracking-widest text-sm px-6 py-3 hover:bg-white transition-colors cursor-pointer"
            >
              Try again
            </button>
            <button
              data-testid="geo-continue-sweden-button"
              onClick={() => navigate("/se/")}
              className="border border-brand-border text-slate-100 font-display font-bold uppercase tracking-widest text-sm px-6 py-3 hover:border-brand-blue transition-colors cursor-pointer"
            >
              Continue in Sweden
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SessionContext.Provider value={{ ...session, setLanguageId, setReferralCode, consumeCodes, t }}>
      {children}
    </SessionContext.Provider>
  );
}

export const useSession = () => useContext(SessionContext);
